import { Badge, Modal, Spinner, Table } from 'react-bootstrap'
import { useQuery } from '@apollo/client'
import { GET_AUTOMATION_LOGS } from '../../../gql/queries/campaigns'
import { Automation, IAutomationLog, AutomationLogStatus } from '../../../types/campaigns'
import { PhoneProviderActions } from '../../../components/telephony/PhoneProviderActions'

interface Props {
    show: boolean
    onHide: () => void
    automation: Automation | null
}

const statusVariant: Record<AutomationLogStatus, string> = {
    SENT: 'success',
    FAILED: 'danger',
    PENDING: 'warning',
    SKIPPED: 'secondary',
}

export const AutomationLogModal = ({ show, onHide, automation }: Props) => {
    const { data, loading } = useQuery<{ automationLogs: IAutomationLog[] }>(GET_AUTOMATION_LOGS, {
        skip: !show || !automation,
        variables: { automationId: automation?.id },
        fetchPolicy: 'network-only',
    })

    const logs = data?.automationLogs ?? []

    return (
        <Modal show={show} onHide={onHide} size='lg' centered>
            <Modal.Header closeButton>
                <Modal.Title>📋 Sending History — {automation?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                {loading && (
                    <div className='text-center py-4'>
                        <Spinner animation='border' size='sm' className='me-2' />
                        Loading...
                    </div>
                )}
                {!loading && logs.length === 0 && (
                    <div className='text-center text-muted py-5'>
                        <div style={{ fontSize: 48 }}>📭</div>
                        <p className='mt-2'>No sending records yet</p>
                    </div>
                )}
                {!loading && logs.length > 0 && (
                    <Table hover responsive size='sm' className='mb-0'>
                        <thead className='table-light'>
                            <tr>
                                <th>Contact</th>
                                <th>Channel</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log) => (
                                <tr key={log.id}>
                                    <td>
                                        {log.contact ? (
                                            <div className='d-flex flex-column gap-1'>
                                                <span>
                                                    {`${log.contact.firstName} ${log.contact.lastName}`.trim() || log.contact.email || '—'}
                                                </span>
                                                {log.contact.phone && (
                                                    <PhoneProviderActions
                                                        phone={log.contact.phone}
                                                        className='d-inline-flex flex-column align-items-start'
                                                        textClassName='small text-muted'
                                                    />
                                                )}
                                            </div>
                                        ) : '—'}
                                    </td>
                                    <td>
                                        <Badge bg={log.channel === 'EMAIL' ? 'primary' : 'info'} className='text-white'>
                                            {log.channel}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg={statusVariant[log.status] ?? 'secondary'}>{log.status}</Badge>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {new Date(log.sentAt).toLocaleString()}
                                    </td>
                                    <td>
                                        {log.errorMessage
                                            ? <span className='text-danger small'>{log.errorMessage}</span>
                                            : <span className='text-muted'>—</span>
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Modal.Body>
        </Modal>
    )
}
